import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, UserCheck, Lock, Sparkles, FileText } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { Button } from "@/components/ui/button";
import { REVIEW_TIERS } from "@/lib/reviewPricing";

/**
 * AboutPage: concierge mode (2026-10-09).
 *
 * Honesty rule (CLAUDE.md): real facts only. The previous version claimed
 * "15 verified experts", "9 domains", a stale starting price, "thousands of
 * businesses", a Dubai founding story, on-ground presence in seven markets
 * and a GTM team-building service. None of it could be backed, so it was
 * deleted. Prices come from lib/reviewPricing (mirrors the backend).
 */
export default function AboutPage() {
  const principles = [
    {
      icon: UserCheck,
      title: "A named human",
      description: "Every review is done by a person you can name. Today that is the founder.",
    },
    {
      icon: Sparkles,
      title: "AI tools, disclosed",
      description: "Reviewers use AI tools and say so. The calls in the verdict are theirs.",
    },
    {
      icon: Lock,
      title: "Private by default",
      description: "The verdict is yours. A public badge goes up only if you agree to it.",
    },
    {
      icon: FileText,
      title: "List prices",
      description: "One price per turnaround, the same for everyone. No retainer, no proposal.",
    },
  ];

  return (
    <div className="min-h-screen bg-canvas">
      <MarketingNav />

      {/* Hero */}
      <section className="pt-32 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-brand-green/5 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slateline-500 hover:text-ink mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green mb-4">
              The human review layer for AI-built work
            </p>
            <h1 className="font-display text-5xl md:text-6xl mb-6">
              AI builds it.{" "}
              <span className="italic text-brand-green">A person reviews it.</span>
            </h1>
            <p className="text-xl text-slateline-500 leading-relaxed">
              Xpert is a review service for work you built with AI tools. You send
              the code, spec or strategy doc and the AI conversation behind it; a
              named human reviews it and sends back a written verdict.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Where we are today */}
      <section className="py-24 bg-canvas-card/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-4xl mb-6">Where we are today</h2>
              <p className="text-xl text-slateline-500 leading-relaxed mb-6">
                Xpert is early and founder-run. Every async review today is done by
                Ankit Kumar, who founded Xpert. He reviews AI-built code and AI-built
                product and strategy work.
              </p>
              <p className="text-slateline-500 leading-relaxed">
                As demand grows, we add hand-picked specialists for other kinds of
                work. We'd rather tell you who is reviewing than imply a bench we
                don't have.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-6"
            >
              {REVIEW_TIERS.map((t) => (
                <div
                  key={t.key}
                  className="bg-canvas-card border border-slateline-200 p-8 rounded-xl text-center"
                >
                  <div className="font-display text-4xl text-brand-green mb-2">${t.price}</div>
                  <div className="text-slateline-400 text-sm">{t.label} review</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl mb-4">How we work</h2>
            <p className="text-slateline-500 max-w-2xl mx-auto">
              The principles behind every review.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {principles.map((value, index) => (
              <motion.div
                key={value.title}
                className="p-8 bg-canvas-card border border-slateline-200 rounded-xl"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <value.icon className="w-10 h-10 text-brand-green mb-6" strokeWidth={1.8} />
                <h3 className="font-display text-xl mb-3">{value.title}</h3>
                <p className="text-slateline-500 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-canvas-card/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl mb-6">Have something AI built?</h2>
            <p className="text-xl text-slateline-500 max-w-2xl mx-auto mb-8">
              Send it with the conversation behind it and get a written verdict.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/get-verified">
                <Button className="bg-brand-green hover:bg-brand-greenHover text-white font-semibold px-8 py-6 rounded-md">
                  Get your AI-built work reviewed
                </Button>
              </Link>
              <Link to="/experts">
                <Button variant="outline" className="border-slateline-200 hover:bg-slateline-100 px-8 py-6 rounded-md">
                  Browse experts
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
